"use client";

import { useState, useEffect, useRef } from "react";
import { liveQuery } from "dexie";

const NOT_LOADED = Symbol("NOT_LOADED");

/**
 * Remplacement local de useLiveQuery (dexie-react-hooks).
 * Souscrit à un liveQuery Dexie et retourne le résultat réactif.
 *
 * Retourne `undefined` avant le premier résultat (chargement),
 * puis la valeur Dexie (qui peut être `undefined` si l'élément n'existe pas).
 * `hasLoaded` permet de distinguer "en cours de chargement" de "pas trouvé".
 */
export function useLiveQuery<T>(
  querier: () => T | Promise<T>,
  deps?: unknown[]
): T | undefined {
  const [result, setResult] = useState<T | typeof NOT_LOADED>(NOT_LOADED);
  const querierRef = useRef(querier);
  querierRef.current = querier;

  useEffect(() => {
    setResult(NOT_LOADED);
    const observable = liveQuery(() => querierRef.current());
    const subscription = observable.subscribe({
      next: (value) => setResult(value),
      error: (err) => console.error("useLiveQuery error:", err),
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps ?? []);

  return result === NOT_LOADED ? undefined : result;
}

/**
 * Variante qui expose aussi l'état de chargement.
 * Utile pour distinguer "chargement" de "pas trouvé".
 */
export function useLiveQueryWithStatus<T>(
  querier: () => T | Promise<T>,
  deps?: unknown[]
): { data: T | undefined; isLoading: boolean } {
  const [result, setResult] = useState<T | typeof NOT_LOADED>(NOT_LOADED);
  const querierRef = useRef(querier);
  querierRef.current = querier;

  useEffect(() => {
    setResult(NOT_LOADED);
    const observable = liveQuery(() => querierRef.current());
    const subscription = observable.subscribe({
      next: (value) => setResult(value),
      error: (err) => console.error("useLiveQuery error:", err),
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps ?? []);

  return {
    data: result === NOT_LOADED ? undefined : result,
    isLoading: result === NOT_LOADED,
  };
}
